// @flow
import * as React from 'react';
import { fetchApplications } from 'api/ats';
import type { ListingApplicationMini, ListingApplicationType } from 'api/ats';
import scrollIntoView from 'utils/scrollIntoView';
import * as localStorage from 'utils/localStorage';
import {
  getNotificationStatus,
  setNotificationStatus,
  notificationIsDismissed,
  dismissNotification,
  NOTIFICATIONS
} from 'utils/localNotifications';

const { useEffect, useState, useCallback } = React;

type ApplicationSearch = (
  string,
  ?string,
  ListingApplicationType
) => {
  loading: boolean,
  applications: ?Array<ListingApplicationMini>,
  count: ?number,
  pages: ?number,
  pageIndex: number,
  perPage: ?number,
  setPageIndex: number => void,
  search: Function,
  searchIndex: number
};

export const useApplicationSearch: ApplicationSearch = (
  orgId,
  listingId,
  listingType
) => {
  const [loading, setLoading] = useState(true);
  const [pageIndex, updatePageIndex] = useState(0);
  const [applications, setApplications] = useState();
  const [count, setCount] = useState();
  const [pages, setPages] = useState();
  const [perPage, setPerPage] = useState();
  const [searchIndex, setSearchIndex] = useState(0);

  const setPageIndex = (index: number) => {
    updatePageIndex(index);
    scrollIntoView();
  };

  const search = useCallback(
    () => {
      const params = {
        id: listingId,
        page: pageIndex + 1,
        type: listingType
      };

      fetchApplications(orgId, params).then(response => {
        setApplications(response.listingApplications);
        setCount(response.count);
        setPages(response.lastPage);
        setPerPage(response.perPage);
        setLoading(false);
        setSearchIndex(prev => prev + 1);
      });
    },
    [listingId, listingType, orgId, pageIndex]
  );

  useEffect(
    () => {
      search();
    },
    [listingId, listingType, orgId, pageIndex, search]
  );

  return {
    loading,
    applications,
    count,
    pages,
    pageIndex,
    perPage,
    setPageIndex,
    search,
    searchIndex
  };
};

export const atsSurveyBannerIsDismissed = () =>
  notificationIsDismissed(NOTIFICATIONS.ORG_ADMIN_ATS_SURVEY_BANNER);

export const atsSurveyBannerShouldDisplay = () =>
  getNotificationStatus(NOTIFICATIONS.ORG_ADMIN_ATS_SURVEY_BANNER) === 'NEW';

export const dismissAtsSurveyBanner = () =>
  dismissNotification(NOTIFICATIONS.ORG_ADMIN_ATS_SURVEY_BANNER);

export const showAtsSurveyBanner = () => {
  if (!atsSurveyBannerIsDismissed()) {
    setNotificationStatus(NOTIFICATIONS.ORG_ADMIN_ATS_SURVEY_BANNER, 'NEW');
  }
};

export const getDefaultApplyAckText = (
  locale: string,
  orgName: string,
  listingName?: string = '<p><br></p>'
) =>
  ({
    en: `<p>Your resume and application materials have been successfully submitted ${
      orgName ? `to ${orgName}` : ''
    } for the following listing.</p>${listingName}<p>Best Regards,</p><p>The Idealist Support Team</p>`,

    es: `<p>Tu currículum y otros materiales para tu postulación se han enviado correctamente ${
      orgName ? `a ${orgName}` : ''
    } para la siguiente oportunidad.</p>${listingName}<p>Suerte,</p><p>El Equipo de Idealist</p>`,

    pt: `<p>O seu currículo e material de inscrição foram submetidos com sucesso ${
      orgName ? `para ${orgName}` : ''
    } para o seguinte anúncio.</p>${listingName}<p>Saudações,</p><p>Equipe de Apoio do Idealist</p>`
  }[locale]);

const HAS_USED_ATS = 'hasUsedAts';

export const usedAts = () => localStorage.setItem(HAS_USED_ATS, true);

export const hasUsedAts = () => localStorage.getItem(HAS_USED_ATS);
